import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Mail, MapPin, MessageCircle, Rocket } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/neptune/Navbar";
import Footer from "../components/neptune/Footer";
import PageHeader from "../components/neptune/PageHeader";
import { useContent } from "../context/ContentContext";

const NEPTUNE_EMAIL = "hello@neptunepay.io";

const BUSINESS_TYPES = [
  "E-commerce",
  "Marketplace",
  "SaaS",
  "Travel",
  "Fintech",
  "Enterprise",
  "Other",
];

const VOLUMES = ["<$50k", "$50k–$250k", "$250k–$1M", "$1M–$5M", "$5M+"];

const initialForm = {
  name: "",
  email: "",
  company: "",
  phone: "",
  business_type: "",
  monthly_volume: "",
  message: "",
};

export default function Contact() {
  const { t } = useContent();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.company.trim()) {
      setError("Please fill in name, work email and company.");
      return;
    }
    if (!form.business_type || !form.monthly_volume) {
      setError("Please pick a business type and estimated monthly volume.");
      return;
    }
    setSubmitting(true);
    try {
      const subject = `Neptune — New contact from ${form.company || form.name}`;
      const body = [
        `Name: ${form.name}`,
        `Email: ${form.email}`,
        `Company: ${form.company}`,
        form.phone ? `Phone: ${form.phone}` : null,
        `Business type: ${form.business_type}`,
        `Monthly volume: ${form.monthly_volume}`,
        `Source: contact-page`,
        "",
        "Message:",
        form.message || "(no message)",
      ]
        .filter(Boolean)
        .join("\n");
      const mailto = `mailto:${NEPTUNE_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailto;
      setSubmitted(true);
    } catch (err) {
      setError("Could not open your email client. Please email hello@neptunepay.io directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="contact-page"
      className="relative min-h-screen bg-[#0a0a0a] text-white font-body overflow-x-hidden"
    >
      <Navbar />

      <PageHeader
        eyebrow={t("contact.header.eyebrow", "Contact")}
        title={t("contact.header.title", "Let's talk payments.")}
        sub={t("contact.header.sub", "Tell us about your business — we route every inquiry to a payments specialist within one business day.")}
      />

      <section className="relative py-12 sm:py-20 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Side info */}
          <aside className="lg:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">
              — Get in touch
            </div>
            <h2 className="font-display mt-5 text-3xl sm:text-4xl font-semibold tracking-tighter chrome-text leading-tight">
              {t("contact.aside.title", "We respond, fast.")}
            </h2>
            <div className="mt-10 space-y-7">
              <ContactBlock
                Icon={Mail}
                label="Sales"
                value={t("contact.aside.email_sales", "hello@neptunepay.io")}
              />
              <ContactBlock
                Icon={MessageCircle}
                label="Support"
                value={t("contact.aside.email_support", "hello@neptunepay.io")}
              />
              <ContactBlock
                Icon={MapPin}
                label="Headquarters"
                value={t("contact.aside.hq", "Dubai · London · Singapore")}
              />
            </div>

            {/* Apply now — merchant application CTA */}
            <div className="mt-10 rounded-2xl border border-[#00E5FF]/20 bg-gradient-to-br from-[#00E5FF]/[0.06] to-[#0055FF]/[0.04] p-5">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#00E5FF]">
                <Rocket className="h-3.5 w-3.5" strokeWidth={1.8} />
                Ready to start?
              </div>
              <h3 className="mt-3 font-display text-xl text-white tracking-tight leading-snug">
                Skip the email back-and-forth.
              </h3>
              <p className="mt-2 text-[13px] text-zinc-400 leading-relaxed">
                Create your Neptune account in seconds — your back office opens instantly.
              </p>
              <Link
                to="/signup"
                data-testid="contact-get-started-btn"
                className="mt-4 group inline-flex items-center gap-1.5 rounded-full bg-white text-black hover:bg-zinc-200 transition-colors px-4 py-2 text-[12.5px] font-medium"
              >
                Get Started
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-7 lg:col-start-6">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                data-testid="contact-success"
                className="rounded-3xl border border-white/[0.06] bg-gradient-to-b from-white/[0.04] to-white/[0.01] p-10 sm:p-14 text-center"
              >
                <div className="mx-auto h-14 w-14 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#0055FF] flex items-center justify-center shadow-[0_0_30px_rgba(0,85,255,0.5)]">
                  <ArrowRight className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-display mt-7 text-3xl sm:text-4xl font-semibold tracking-tighter chrome-text">
                  Thanks{form.name ? `, ${form.name.split(" ")[0]}` : ""}.
                </h3>
                <p className="mt-4 text-[15px] text-zinc-400 max-w-md mx-auto leading-relaxed">
                  A Neptune specialist will follow up within one business day
                  with a tailored next step.
                </p>
              </motion.div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="rounded-3xl border border-white/[0.06] bg-gradient-to-b from-white/[0.03] to-white/[0.01] p-7 sm:p-10"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Full name"
                    placeholder="Alex Morgan"
                    value={form.name}
                    onChange={(v) => setField("name", v)}
                    testId="contact-input-name"
                  />
                  <Input
                    label="Work email"
                    placeholder="alex@company.com"
                    type="email"
                    value={form.email}
                    onChange={(v) => setField("email", v)}
                    testId="contact-input-email"
                  />
                  <Input
                    label="Company"
                    placeholder="Acme Inc."
                    value={form.company}
                    onChange={(v) => setField("company", v)}
                    testId="contact-input-company"
                  />
                  <Input
                    label="Phone (optional)"
                    placeholder="+1 555 000 1234"
                    value={form.phone}
                    onChange={(v) => setField("phone", v)}
                    testId="contact-input-phone"
                  />
                </div>

                <div className="mt-6">
                  <Label>Business type</Label>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {BUSINESS_TYPES.map((b) => {
                      const active = form.business_type === b;
                      return (
                        <button
                          key={b}
                          type="button"
                          onClick={() => setField("business_type", b)}
                          data-testid={`contact-business-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className={`px-3.5 py-2 rounded-full text-[13px] border transition-colors ${
                            active
                              ? "border-[#00E5FF]/40 bg-white/[0.06] text-white"
                              : "border-white/[0.08] bg-white/[0.015] text-zinc-300 hover:bg-white/[0.04]"
                          }`}
                        >
                          {b}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-6">
                  <Label>Estimated monthly volume</Label>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {VOLUMES.map((v) => {
                      const active = form.monthly_volume === v;
                      return (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setField("monthly_volume", v)}
                          data-testid={`contact-volume-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className={`px-3.5 py-2 rounded-full text-[13px] border transition-colors ${
                            active
                              ? "border-[#00E5FF]/40 bg-white/[0.06] text-white"
                              : "border-white/[0.08] bg-white/[0.015] text-zinc-300 hover:bg-white/[0.04]"
                          }`}
                        >
                          {v}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-6">
                  <Label>Tell us about your project (optional)</Label>
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={(e) => setField("message", e.target.value)}
                    placeholder="Current stack, pain points, target markets…"
                    data-testid="contact-input-message"
                    className="mt-2 w-full rounded-xl bg-white/[0.02] border border-white/[0.08] px-3.5 py-3 text-[14px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#00E5FF]/40 focus:bg-white/[0.04] transition-colors resize-none"
                  />
                </div>

                {error && (
                  <div
                    className="mt-4 text-[13px] text-rose-400"
                    data-testid="contact-error"
                  >
                    {error}
                  </div>
                )}

                <div className="mt-7 flex items-center justify-between gap-3">
                  <span className="text-[12px] text-zinc-500">
                    We respect your privacy.
                  </span>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-btn"
                    className="inline-flex items-center gap-1.5 rounded-full bg-white text-black hover:bg-zinc-200 disabled:opacity-60 disabled:cursor-not-allowed transition-colors px-6 py-3 text-[13.5px] font-medium"
                  >
                    {submitting ? "Sending…" : "Send message"}
                    {!submitting && <ArrowRight className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}

function Label({ children }) {
  return (
    <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
      {children}
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="mt-2 w-full rounded-xl bg-white/[0.02] border border-white/[0.08] px-3.5 py-2.5 text-[14px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#00E5FF]/40 focus:bg-white/[0.04] transition-colors"
      />
    </div>
  );
}

function ContactBlock({ Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-zinc-500 mt-0.5" strokeWidth={1.5} />
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
          {label}
        </div>
        <div className="mt-1 text-[14.5px] text-white">{value}</div>
      </div>
    </div>
  );
}
